import Vue from 'vue';
import { Plugin } from '@nuxt/types';
import { jwPlayerCdnUrl, jwPlayerKey, apiUrl } from '@root/modules/podcast/config/player.config';
import { loadScripts } from '@root/common/utils/scriptLoader';
import { JwPlayer } from '@root/modules/podcast/utils/jwPlayer';
import { ContainerPlayer } from '@root/modules/podcast/utils/ContainerPlayer';
import { PlayerPlugin, Player, ContainerAudioItem, AudioItem } from '@root/modules/podcast/types/player';
import { clearLastPlayed, updateLastPlayed } from '@root/modules/podcast/utils/storage';
import getMediaData from '@root/modules/podcast/utils/getMediaData';
import { getPlayTimeFromStorage, saveLastPlayed } from '@root/modules/podcast/utils/storage';
import buildPodcastUrl from '@root/modules/podcast/utils/buildPodcastUrl';
import { Podcast } from '@root/modules/podcast/types/podcast';
import PodcastEpisodeService from '@root/modules/podcast/services/podcastEpisode.service';
import { limit } from '@root/modules/podcast/config/podcast.config';
import { createListenedId, getListenedDuration, handleListenedDurationUpdate } from '@root/modules/podcast/utils/customerData';

interface ContainerPlayerChangedEvent {
  audioItem: ContainerAudioItem;
  isPlaying: boolean;
  playbackRate: number;
}

interface CustomWindow extends Window {
  onPlayerStateChanged: (event: ContainerPlayerChangedEvent) => void;
}

declare let window: CustomWindow;

// Inject into Vue instances (this.$player)
declare module 'vue/types/vue' {
  interface Vue {
    $player: PlayerPlugin;
  }
}

const playerPlugin: Plugin = async (ctx, inject) => {
  const { store, app } = ctx;
  let player: Player | null = null;
  let customerDataSaveInterval: any = undefined;

  const setup: PlayerPlugin['setup'] = async (episode, isResuming = false) => {
    let audioItem: AudioItem | null = null;

    const customerToken = store.state.piano.token;
    const fakeDomainEnabled = app.$config.FAKE_DOMAIN_ENABLED;
    const { domain } = app.$channelConfig('settings');
    const mediaData = await getMediaData([episode], customerToken);

    if (mediaData?.length) {
      const { assetType, assetUrl } = mediaData[0];

      if (assetType && assetUrl) {
        const audioId = assetType === 'radio' ? episode.id : episode.content.leadElement.content[0].attrs.id;

        audioItem = {
          file: assetUrl,
          title: (episode.content.title.text as string) || '',
          description: (episode.content.lead?.text as string) || '',
          descriptionLink: buildPodcastUrl({ article: episode, domain, fakeDomainEnabled }),
          img: { id: episode.metaImage?.id },
          audioId,
          audioPosition: assetType !== 'radio' ? (getPlayTimeFromStorage(audioId)?.playTime ?? 0) : 0,
          assetType,
          articleId: episode.id,
          parentTitle: episode.primaryCategory.parentCategory?.name,
          categoryTitle: episode.primaryCategory.name,
          author: episode.primaryCategory.attributes?.categoryAuthor,
          autoPlay: !episode.paused,
        };

        // saveLastPlayed item data for resume playing
        saveLastPlayed(episode);
      }
    }

    if (!audioItem) {
      return;
    }

    const playerSetup = await player?.setup(audioItem, isResuming);

    if (!playerSetup) {
      return;
    }

    // Wait for Piano init before initialising customer data
    if (store.state.piano.isScriptInited) {
      const token = store.state.piano.token;
      initialiseCustomerData(token);
    } else if (!store.state.piano.isScriptInited) {
      store.watch(
        (state) => state.piano.isScriptInited,
        async () => {
          const token = store.state.piano.token;
          initialiseCustomerData(token);
        }
      );
    }
  };

  const initialiseCustomerData: PlayerPlugin['initialiseCustomerData'] = async (token?: string) => {
    if (!token) {
      return;
    }

    const articleId = String(player?.state.currentlyPlaying.articleId);

    try {
      createListenedId(articleId, token);
    } catch (e) {
      console.log(e);
    }

    await getListenedDuration([articleId], token);

    clearInterval(customerDataSaveInterval);

    customerDataSaveInterval = setInterval(async () => {
      try {
        handleListenedDurationUpdate(articleId, String(player?.state.currentlyPlaying.audioPosition), token);
      } catch (e) {
        console.log(e);
      }
    }, 5 * 1000);
  };

  const togglePlayPauseIfNeeded: PlayerPlugin['togglePlayPauseIfNeeded'] = ({ audioItemId, playlist }) => {
    const isCurrentEpisodeActive = playlist
      ? Boolean(playlist.find((item) => item.audioId === player?.state.currentlyPlaying.audioId))
      : player?.state.currentlyPlaying.audioId === String(audioItemId);

    if (isCurrentEpisodeActive) {
      if (player?.state.isPlaying) {
        player?.pause();
      } else {
        player?.play();
      }

      return true;
    }

    return false;
  };

  const play: PlayerPlugin['play'] = () => {
    updateLastPlayed({ paused: false });

    player?.play();
  };

  const pause: PlayerPlugin['pause'] = () => {
    updateLastPlayed({ paused: true });

    player?.pause();
  };

  const seek: PlayerPlugin['seek'] = (position) => {
    player?.seek(position);
  };

  const seekBack: PlayerPlugin['seekBack'] = () => {
    player?.seekBack();
  };

  const seekForward: PlayerPlugin['seekForward'] = () => {
    player?.seekForward();
  };

  const remove: PlayerPlugin['remove'] = () => {
    // Clear last played
    clearLastPlayed();

    // Stop customer data interval
    clearInterval(customerDataSaveInterval);

    player?.remove();
  };

  const changeTitle: PlayerPlugin['changeTitle'] = (title) => {
    player?.changeTitle(title);
  };

  const setPlaybackRate: PlayerPlugin['setPlaybackRate'] = (playbackRate) => {
    player?.setPlaybackRate(playbackRate);
  };

  const prev: PlayerPlugin['prev'] = async () => {
    const playlist: Podcast[] = store.getters['podcast/getActivePlaylist'];
    const current = playlist.find((item) => item.id === player?.state.currentlyPlaying.articleId);
    if (current) {
      const prev = playlist[playlist.indexOf(current) - 1];
      if (prev) {
        await setup(prev);
      }
    }
  };

  const next: PlayerPlugin['next'] = async () => {
    const playlist: Podcast[] = store.getters['podcast/getActivePlaylist'];
    const current = playlist.find((item) => item.id === player?.state.currentlyPlaying.articleId);
    if (current) {
      const next = playlist[playlist.indexOf(current) + 1];
      if (next) {
        await setup(next);

        // Check if there's more episodes to load in playlist after starting to play last playlist item
        if (next.id === playlist[playlist.length - 1].id) {
          const items = await new PodcastEpisodeService().fetch({ categoryId: [next.primaryCategory.id], offset: playlist.length, limit });
          if (items?.length) {
            store.dispatch('podcast/setPlaylist', [...playlist, ...items]);
          }
        }
      }
    }
  };

  const toggleBigPlayer: PlayerPlugin['toggleBigPlayer'] = () => {
    player!.toggleBigPlayer();
  };

  const useContainerPlayer = false;
  if (useContainerPlayer) {
    player = new ContainerPlayer(apiUrl.mediaApiImageCropperUrl);

    store.watch(
      (state) => state.piano.access,
      async () => {
        (player as ContainerPlayer).setCustomerToken(store.state.piano.token);
      }
    );

    const customerToken = store.state.piano.token;

    if (customerToken) {
      (player as ContainerPlayer).setCustomerToken(customerToken);
    }

    window.onPlayerStateChanged = (event) => {
      const audioItem = event.audioItem;

      player!.setState({
        file: audioItem.url,
        audioId: audioItem.audioId,
        title: audioItem.title,
        description: audioItem.description,
        img: {
          plainSrc: audioItem.imgUrl,
        },
        // Placeholder value. Is not used in ContainerPlayer setup
        assetType: 'podcasts',
        articleId: audioItem.articleId,
        audioDuration: audioItem.audioDuration,
        audioPosition: audioItem.position,
        descriptionLink: audioItem.descriptionLink,
      });

      if (player!.state.currentlyPlaying.playbackRate !== event.playbackRate) {
        player!.setPlaybackRate(event.playbackRate);
      }

      if (player!.state.isPlaying !== event.isPlaying) {
        player!.state.isPlaying = event.isPlaying;
      }
    };
  } else {
    await loadScripts(jwPlayerCdnUrl, { async: true });

    player = new JwPlayer(jwPlayerKey, apiUrl.mediaApiImageCropperUrl);
  }

  // Listen for player complete event to advance to next playlist item
  window.addEventListener('onPlayerComplete', () => {
    // Store 100% playback to customer data for logged in users
    const token = store.state.piano.token;
    if (token) {
      const articleId = String(player!.state.currentlyPlaying.articleId);
      handleListenedDurationUpdate(articleId, String(player?.state.currentlyPlaying.audioDuration), token);
    }

    next();
  });

  const playerInject: PlayerPlugin = {
    state: Vue.observable(player!.state),
    setup,
    initialiseCustomerData,
    remove,
    play,
    pause,
    seek,
    seekBack,
    seekForward,
    changeTitle,
    setPlaybackRate,
    prev,
    next,
    togglePlayPauseIfNeeded,
    toggleBigPlayer,
  };

  inject('player', playerInject);
};

export default playerPlugin;
